import React, { useContext, useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import ReactPlayer from "react-player";
import Slider from "react-slick";
import youTubePlayButton from "../../assets/newImages/Play.svg";
import { Next, Previous } from "../../components/Icons/Icons";
import { GlobalContext } from "../../components/globalContext/GlobalContext";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "./partnerSlider.scss";
import { getYouTubeVideoId } from "../../utils/helper";

// WHITE-FLASH FIX: while a video initialises (ReactPlayer lazy-loads its
// player chunk behind a `null` Suspense fallback, then YouTube/Vimeo/Synthesia
// load their own iframe) nothing paints inside the container, so the page's
// white background shows through. Painting the containers black turns that
// gap into the intended "thumbnail -> black -> video" transition.
const BLACK_BG_STYLE = { backgroundColor: "#000" };

// Synthesia's iframe document is white until its player has rendered, and
// `onLoad` fires before that. The iframe stays invisible (container is black)
// until it has loaded + this short delay. Increase if you still see white
// inside the Synthesia player; decrease for a snappier reveal.
const SYNTHESIA_REVEAL_DELAY_MS = 400;
// Safety net: never leave the iframe invisible if `onLoad` never fires.
const SYNTHESIA_REVEAL_MAX_MS = 8000;

function SynthesiaIframe({ src, title }) {
  const [revealed, setRevealed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const safetyTimer = setTimeout(
      () => setRevealed(true),
      SYNTHESIA_REVEAL_MAX_MS,
    );
    return () => {
      clearTimeout(safetyTimer);
      clearTimeout(timerRef.current);
    };
  }, []);

  const handleLoad = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => setRevealed(true),
      SYNTHESIA_REVEAL_DELAY_MS,
    );
  };

  return (
    <iframe
      src={src}
      title={title}
      allow="autoplay; encrypted-media"
      allowFullScreen
      frameBorder="0"
      loading="lazy"
      onLoad={handleLoad}
      style={{
        backgroundColor: "#000",
        opacity: revealed ? 1 : 0,
        transition: "opacity 200ms ease",
      }}
    ></iframe>
  );
}

export default function PartnerCarousel(props) {
  const { globalState } = useContext(GlobalContext);
  const slider = React.useRef(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const playerRefs = useRef({});
  const [alreadyReArranged, setAlreadyReArranged] = useState(false)
  const [carouselData, setCarouselData] = useState([]);
  // Synthesia lazy-loading: track which Synthesia videos the user has actually
  // clicked. Their heavy iframe is only mounted after that first click.
  const [loadedSynthesiaVideos, setLoadedSynthesiaVideos] = useState({});

  // const moveLastItemToSecondPosition = (arr) => {
  //   if (arr.length < 2) return arr;
  //   const lastItem = arr[arr.length - 1];
  //   const restItems = arr.slice(0, arr.length - 1);
  //   return [restItems[0], lastItem, ...restItems.slice(1)];
  // };

  // Warm up the connection to Synthesia as soon as the carousel mounts, so the
  // first click-to-load resolves faster. Idempotent: skips existing links.
  useEffect(() => {
    const links = [];
    [
      ["preconnect", "https://share.synthesia.io"],
      ["dns-prefetch", "https://share.synthesia.io"],
    ].forEach(([rel, href]) => {
      if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
      links.push(link);
    });
    return () => {
      links.forEach((link) => link.parentNode && link.parentNode.removeChild(link));
    };
  }, []);

  useEffect(() => {
    setCarouselData(props.carouselData);
  }, [props.carouselData, props]);

  useEffect(() => {
    const handleResize = () => {
      if (!slider.current) return;

      const width = window.innerWidth;
      if (width <= 730) {
        setAlreadyReArranged(true)
        // shift left by 1 when showing 2 slides
        slider.current.slickGoTo(1, true);
      }
      else if (width > 730) {
        if (alreadyReArranged) {
          setAlreadyReArranged(false)
          // shift right by 1 when showing 2 slides
          slider.current.slickGoTo(-1, true);
        }
        else {
          slider.current.slickGoTo(0, true);
        }
      }
      slider.current.innerSlider?.onWindowResized();
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [carouselData, alreadyReArranged]);

  const handlePlay = (id) => {
    // Pause all other videos safely
    Object.keys(playerRefs.current).forEach((videoId) => {
      if (videoId !== id && playerRefs.current[videoId]) {
        const internalPlayer =
          playerRefs.current[videoId].getInternalPlayer?.();
        if (internalPlayer && typeof internalPlayer.pauseVideo === "function") {
          internalPlayer.pauseVideo(); // Only call if it's a YouTube/Vimeo player
        } else if (typeof playerRefs.current[videoId].pause === "function") {
          playerRefs.current[videoId].pause(); // Fallback: use ReactPlayer's own pause
        }
      }
    });

    // Set the new playing video
    setPlayingVideo(id);
  };

  const handlePause = (id) => {
    if (playingVideo === id) {
      setPlayingVideo(null);
    }
  };

  const handleEnded = () => {
    setPlayingVideo(null);
  };

  // Single click: marks the Synthesia video as "wanted", which mounts its
  // iframe with autoplay=1 so playback starts immediately with audio (the
  // click is the user gesture browsers require for unmuted autoplay).
  const handleSynthesiaPlay = (id) => {
    handlePlay(id);
    setLoadedSynthesiaVideos((prev) => ({ ...prev, [id]: true }));
  };

  const NextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <span
        style={{ ...style }}
        className={className}
        onClick={onClick}
        role="button"
      >
        <Next />
      </span>
    );
  };
  const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <span
        style={{ ...style }}
        className={className}
        onClick={onClick}
        role="button"
      >
        <Previous />
      </span>
    );
  };

  const renderMedia = (mediaLink, index, type) => {
    if (
      mediaLink.mediaType === "SOCIAL_MEDIA" ||
      mediaLink.mediaType === "VIMEO_MEDIA"
    ) {
      return (
        <div
          className="carousel-player-wrap"
          key={mediaLink.id}
          style={BLACK_BG_STYLE}
        >
          <ReactPlayer
            ref={(player) => (playerRefs.current[mediaLink.id] = player)}
            className="carousel-react-player"
            style={BLACK_BG_STYLE}
            url={mediaLink.url}
            playing={playingVideo === mediaLink.id}
            light={
              type === "vimeo"
                ? `https://vumbnail.com/${mediaLink.url?.split("/")[4]}.jpg`
                : `https://img.youtube.com/vi/${getYouTubeVideoId(mediaLink?.url)}/hqdefault.jpg`
            }
            playIcon={
              <div
                onClick={() => handlePlay(mediaLink.id)}
                className="carousel-play-icon"
              >
                <img
                  className="play-button"
                  src={youTubePlayButton}
                  alt="play icon"
                />
              </div>
            }
            width="100%"
            height="100%"
            controls
            onPause={() => handlePause(mediaLink.id)}
            onEnded={handleEnded}
            onPlay={() => handlePlay(mediaLink.id)}
          />
        </div>
      );
    }
    if (mediaLink.mediaType === "SYNTHESIA_MEDIA") {
      const rawUrl = mediaLink?.url;

      if (!rawUrl) return null;

      const uuidMatch = rawUrl.match(
        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/,
      );

      if (!uuidMatch) return null;

      const videoId = uuidMatch[0];

      // CORRECT embed URL. autoplay=1 only applies to the click-to-load mount
      // below, so the video starts playing from the user's own click.
      const embedUrl = `https://share.synthesia.io/embeds/videos/${videoId}?autoplay=1`;

      // PERFORMANCE: the Synthesia embed takes ~10s to fully load, so it is
      // NOT mounted on render. Until the user clicks, a lightweight poster
      // placeholder stands in; the iframe mounts on that single click and
      // starts playing immediately via autoplay=1.
      if (!loadedSynthesiaVideos[mediaLink.id]) {
        return (
          <div
            className="synthesia-video-container"
            key={mediaLink.id}
            style={BLACK_BG_STYLE}
          >
            <div
              className="synthesia-lazy-placeholder"
              style={{
                width: "100%",
                height: "100%",
                aspectRatio: "16 / 9",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: "#10131f",
                overflow: "hidden",
              }}
              role="button"
              tabIndex={0}
              aria-label={`Play ${mediaLink?.title || "Synthesia video"}`}
              onClick={() => handleSynthesiaPlay(mediaLink.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSynthesiaPlay(mediaLink.id);
                }
              }}
            >
              {/* Real Synthesia thumbnail, straight from Synthesia's public
                  CDN — no iframe or API call needed. If a thumbnail was
                  supplied in the data, prefer it. If the URL 404s, the
                  onError handler hides the image and the dark background
                  shows instead. */}
              <img
                className="synthesia-thumbnail"
                src={
                  mediaLink?.thumbnailUrl ||
                  `https://synthesia-ttv-data.s3-eu-west-1.amazonaws.com/video_data/${videoId}/thumbnails/default.jpg`
                }
                alt={mediaLink?.title || "Synthesia video"}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <img
                className="play-button"
                src={youTubePlayButton}
                alt="play icon"
                style={{ position: "relative", zIndex: 1 }}
              />
            </div>
          </div>
        );
      }

      return (
        <div
          className="synthesia-video-container"
          key={mediaLink.id}
          style={BLACK_BG_STYLE}
        >
          <SynthesiaIframe
            src={embedUrl}
            title={mediaLink?.title || "Synthesia Video"}
          />
        </div>
      );
    }

    if (mediaLink.mediaType === "IMAGE") {
      return (
        <img
          src={mediaLink.url}
          alt="carousel item"
          className="carousel-image"
          key={mediaLink.id}
        />
      );
    }
    return null;
  };

  const settings = {
    infinite: true,
    slidesToShow:
      window.innerWidth >= 2560
        ? carouselData?.length > 4
          ? 4
          : carouselData?.length
        : carouselData?.length > 3
          ? 3
          : carouselData?.length,
    slideToScroll: 3,
    autoplay: false,
    dots: false,
    swipeToSlide: true,
    swipe: true,
    speed: 500,
    // autoplay: true,
    autoplaySpeed: 2000,
    arrows: false,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    className: "slider variable-width",
    responsive: [
      {
        //  below 450px show single card
        breakpoint: 450,
        settings: {
          centerMode: true,
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
          speed: 500,
        },
      },
      {
        breakpoint: 730,
        settings: {
          centerMode: false,
          slidesToShow: carouselData?.length > 2 ? 2 : 1,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
          speed: 500,
        },
      },
    ],
  };

  useEffect(() => {
    let playPauseBtnList = {};
    props?.carouselData?.forEach((element) => {
      playPauseBtnList[element?.id] = false;
    });
  }, [props.carouselData]);

  return (
    <motion.div
      className="h-100"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.8,
        delay: 0.5,
        ease: [0, 0.71, 0.2, 1.01],
      }}
    >
      {carouselData?.length > 0 ? (
        <div
          className={`partner-slider-container${carouselData?.length === 1 ? " slider-single" : ""}${carouselData?.length === 2 ? " slider-container-length-two" : ""}`}
        >
          {!globalState.isMobile &&
            carouselData &&
            carouselData?.length > 3 && (
              <PrevArrow
                key={"previousArrow"}
                onClick={() => {
                  slider?.current?.slickPrev();
                  setPlayingVideo("");
                }}
                className="cursor-pointer previous-arrow"
              />
            )}

          <Slider ref={slider} {...settings}>
            {carouselData &&
              carouselData.map((data, i) => {
                if (
                  data?.mediaLink?.mediaType === "SOCIAL_MEDIA" &&
                  data.mediaLink.url
                ) {
                  return (
                    <React.Fragment key={`${data.id}-SOCIAL_MEDIA`}>
                      <div className="slide ">
                        <div className="carousel-media">
                          {renderMedia(data?.mediaLink, i, "youtube")}
                        </div>
                        <span className="slide-title-hidden">{data.title}</span>
                      </div>
                      <span className="slide-title">
                        {data.titleLink ? (
                          <a
                            href={data.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data.title}
                          </a>
                        ) : (
                          data.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                } else if (
                  data?.mediaLink?.mediaType === "VIMEO_MEDIA" &&
                  data.mediaLink.url
                ) {
                  return (
                    <React.Fragment key={`${data.id}-VIMEO_MEDIA`}>
                      <div className="slide">
                        <div className="carousel-media">
                          {renderMedia(data?.mediaLink, i, "vimeo")}
                        </div>
                        <span className="slide-title-hidden">{data.title}</span>
                      </div>
                      <span className="slide-title">
                        {data.titleLink ? (
                          <a
                            href={data.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data.title}
                          </a>
                        ) : (
                          data.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                } else if (
                  data?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
                  data.mediaLink.url
                ) {
                  return (
                    <React.Fragment key={`${data.id}-SYNTHESIA_MEDIA`}>
                      <div className="slide ">
                        <div className="carousel-media">
                          {renderMedia(data?.mediaLink, i, "synthesia")}
                        </div>
                        <span className="slide-title-hidden">{data.title}</span>
                      </div>
                      <span className="slide-title">
                        {data.titleLink ? (
                          <a
                            href={data.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data.title}
                          </a>
                        ) : (
                          data.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                } else {
                  return (
                    <React.Fragment key={`${data?.id}-image`}>
                      <div className="slide ">
                        <div className="carousel-media">
                          <img
                            src={data?.mediaLink?.url}
                            alt={data?.title}
                            className="carousel-image "
                          />
                        </div>
                        <span className="slide-title-hidden">{data?.title}</span>
                      </div>
                      <span className="slide-title">
                        {data?.titleLink ? (
                          <a
                            href={data.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data.title}
                          </a>
                        ) : (
                          data?.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                }
              })}
          </Slider>
          {/* </div> */}
          {!globalState.isMobile &&
            carouselData &&
            carouselData?.length > 3 && (
              <NextArrow
                key={"nextArrow"}
                onClick={() => {
                  slider?.current?.slickNext();
                  setPlayingVideo("");
                }}
                className="cursor-pointer next-arrow"
              />
            )}
        </div>
      ) : (
        <div className="empty-partner" style={{ height: "100px" }}>
          No partner items available at the moment.
        </div>
      )}
    </motion.div>
  );
}
